import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReduxControls } from "rhombus-react";
import {
	FormSection,
	Field,
	touch,
	change,
	getFormValues,
	getFormSyncWarnings
} from "redux-form";

import { noop } from "utils/common";
import { SOCIAL_AD_TYPES } from "utils/constants";
import { getGoogleLanguageOptions } from "selectors/Social2";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { GOOGLE_DEMAND_GEN_IMAGE_CONSTRAINTS } from "social/validators/google";
import ImageFieldArray from "widgets/modals/versions/generate/ImageFieldArray";
import TextFieldArray from "social/TextFieldArray";
import DescriptiveLabel from "social/DescriptiveLabel";
import { mapStringToOption, normalizeOptionToString, useErrorBanner } from "social/utils";

import "./GoogleDemandGenImage.css";

const {
	MAX_INPUTS,
	IMAGE_ACCEPT,
	MAX_LENGTH_BUSINESS_NAME,
	MAX_LENGTH_LANDING_PAGE,
} = GOOGLE_DEMAND_GEN_IMAGE_CONSTRAINTS;

const GOOGLE_DEMAND_GEN_IMAGE_FIELDS = {
	creativeName: {
		name: "name",
		label: "Creative Name",
		className: "full-width",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		showCharCount: true,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showPristineErrors: true,
		required: true,
	},
	images: {
		name: "images",
		label: (
			<DescriptiveLabel
				label="Images"
				description={`(Up to ${MAX_INPUTS.images}, At least 1 landscape (1.91:1) or square (1:1) image required)`}
			/>
		),
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `1-20 images of the following aspect ratios:
			<ul>
				<li>Horizontal (1.91:1, 1200x628 and up recommended).</li>
				<li>Square (1:1, 1200x1200 and up recommended).</li>
				<li>Vertical (4:5, 960x1200 and up recommended) images.</li>
			</ul>`,
			html: true,
		},
		component: ImageFieldArray,
		variableType: "image",
		required: true,
		form: SOCIAL_WIZARD,
		inputPrompt: "Select Image(s)",
		maxAssets: MAX_INPUTS.images,
		modalTitle: `Select Images (up to ${MAX_INPUTS.images})`,
		accept: IMAGE_ACCEPT,
	},
	logos: {
		name: "logos",
		label: (
			<DescriptiveLabel
				label="Logos"
				description={`(Up to ${MAX_INPUTS.logos}, At least 1 required)`}
			/>
		),
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: '1:1 aspect ratio. 1200x1200 and up recommended',
		},
		component: ImageFieldArray,
		variableType: "image",
		required: true,
		form: SOCIAL_WIZARD,
		inputPrompt: "Select Image(s)",
		maxAssets: MAX_INPUTS.logos,
		modalTitle: `Select Images (up to ${MAX_INPUTS.images})`,
		accept: IMAGE_ACCEPT,
	},
	headlines:{
		name: "headlines",
		label: (
			<DescriptiveLabel
				label="Headlines"
				description={`(Up to ${MAX_INPUTS.headlines})`}
			/>
		),
		className: "full-width",
		placeholder: "Enter Headline...",
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `The headline is the first line of your ad and can be up to 40 characters.<br />
			Add up to five headlines that will be combined with your other assets to generate your ads.<br />
			The length of the rendered headline will depend on the site it appears on.<br />
			If it's shortened, it will end with an ellipsis.
			`,
			html: true,
		},
		component: TextFieldArray,
		maxInputs: MAX_INPUTS.headlines,
		addRowLabel: "Add Headline",
		required: true,
	},
	descriptions: {
		name: "descriptions",
		label: (
			<DescriptiveLabel
				label="Descriptions"
				description={`(Up to ${MAX_INPUTS.descriptions})`}
			/>
		),
		className: "full-width",
		placeholder: "Enter Description...",
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `
			<ul>
				<li>
					The description adds to the headline and provides additional context or details.<br />
					It can be up to 90 characters, and may appear after the headline.<br />
					Add up to 5 descriptions that will be combined with any one of your headlines to generate your ads.
				</li>
				<li>
					The length of the rendered description will depend on the site it appears on.<br />
					If it's shortened, it will end with an ellipsis.<br />
					The description doesn't show in all sizes and formats.
				</li>
			</ul>`,
			html: true,
		},
		component: TextFieldArray,
		addRowLabel: "Add Description",
		maxInputs: MAX_INPUTS.descriptions,
		required: true,
	},
	businessName: {
		name: "businessName",
		label: "Business Name",
		className: "full-width",
		placeholder: "Enter Business Name...",
		component: ReduxControls.Input,
		showCharCount: true,
		charMax: MAX_LENGTH_BUSINESS_NAME,
		required: true,
	},
	landingPage:{
		name: "landingPage",
		label: "Landing Page",
		className: "full-width",
		placeholder: "Enter URL...",
		component: ReduxControls.Input,
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: "Redirects to different domains are not permitted",
		},
		showCharCount: true,
		charMax: MAX_LENGTH_LANDING_PAGE,
		required: true,
	},
	mobileLandingPage: {
		name: "mobileLandingPage",
		label: "Mobile Landing Page",
		className: "full-width",
		placeholder: "Enter URL...",
		component: ReduxControls.Input,
		showCharCount: true,
		charMax: MAX_LENGTH_LANDING_PAGE,
	},
	language: {
		name: "language",
		label: "Language",
		component: ReduxControls.Select,
		className: "half-width",
		menuPlacement: "top",
		placeholder: "Select Language...",
		isClearable: false,
		onMenuScrollToBottom: noop,
	},
	cta: {
		name: "cta",
		label: "CTA",
		component: ReduxControls.Select,
		required: true,
		className: "half-width",
		menuPlacement: "top",
		placeholder: "Select CTA...",
		isClearable: false,
		onMenuScrollToBottom: noop,
		normalize: normalizeOptionToString,
		format: mapStringToOption,
	},
};

const IMAGE_FIELD_ARRAYS = [
	"images",
	"logos",
];

const TEXT_FIELD_ARRAYS = [
	"headlines",
	"descriptions"
];

function fieldGenerator(props){
	const {
		adType,
		libraryId,
		showPristineErrors,
		isViewOnly,
		errors,
		languageOptions = [],
		ctaOptions = [],
		touchImageFieldArray,
		showCountError,
		isFieldArrayValid,
		onLanguageChange = noop,
	} = props;

	return (name) => {
		let fieldProps = GOOGLE_DEMAND_GEN_IMAGE_FIELDS[name];

		let extraProps = {
			showPristineErrors,
			disabled: isViewOnly,
		};

		let fieldArrayProps = {
			meta: {
				valid: isFieldArrayValid(name, errors),
				dirty: true,
			},
		};

		fieldProps.key = `${adType}-${name}`;

		if(name === "language"){
			extraProps.options = languageOptions;
			extraProps.onChange = onLanguageChange;
		}
		if(name === "cta"){
			extraProps.options = ctaOptions;
		}

		if(IMAGE_FIELD_ARRAYS.includes(name)){
			return (
				<div
					tabIndex="-1"
					onBlur={() => showCountError(name)}
					key={`${name}-wrapper`}
					onClick={() => touchImageFieldArray(name)}
				>
					<ReduxControls.RowController
						{...fieldProps}
						{...extraProps}
						{...fieldArrayProps}
						libraryId={libraryId}
					/>
				</div>
			);
		}

		if(TEXT_FIELD_ARRAYS.includes(name)) {
			return (
				<ReduxControls.RowController
					{...fieldProps}
					{...extraProps}
					{...fieldArrayProps}
					onBlur={() => showCountError(name)}
				/>
			);
		}

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		return <Field {...fieldProps} {...extraProps} />;
	};
}

export default function GoogleDemandGenImage(props) {
	const adType = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE
		, dispatch = useDispatch()
		, { errors, values } = useSelector(state => ({
			errors: getFormSyncWarnings(SOCIAL_WIZARD)(state)?.[adType] || {},
			values: getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {},
		}))
		, [showFieldCountError, setShowFieldCountError] = useState([])
		, {
			libraryId,
			showPristineErrors,
			isViewOnly,
		} = props
		;

	const locales = useSelector(state => state.social.googleLocales.locales);
	const selectedLanguage = values.language?.id || "en";
	const selectedCTA = values.cta;

	const languageOptions = useSelector(getGoogleLanguageOptions);
	const ctaOptions = locales[selectedLanguage]?.ctas?.map(cta => mapStringToOption(cta.name)) || [];

	function onLanguageChange(alsoValue, value, previousValue){
		/*
			When the user selects a new language, we have to update the CTA value to the equivalent ID,
			which we don't have directly, so we have to find it based on the previous language and CTA name.
		*/
		const prevLocale = previousValue?.id || "en";
		const ctaId = locales[prevLocale]?.ctas?.find(cta => cta.name === selectedCTA)?.id;

		const newLocale = value?.id;
		const equivalentCTA = locales[newLocale]?.ctas?.find(cta => cta.id === ctaId)?.name;

		dispatch(change(SOCIAL_WIZARD, `${adType}.cta`, equivalentCTA));
	}

	function showCountError(name) {
		setShowFieldCountError(prev => [`${name}Count`, ...prev]);
	}

	function touchImageFieldArray(name) {
		dispatch(touch(SOCIAL_WIZARD, `${adType}.${name}`));
	}

	function isFieldArrayValid(name, errors) {
		return !Object.keys(errors).some(field => field.includes(name));
	}

	const showPristine = useMemo(() => {
		return showPristineErrors || showFieldCountError;
	}, [showPristineErrors, showFieldCountError]);

	const errorDisplay = useErrorBanner(adType, showPristine, GOOGLE_DEMAND_GEN_IMAGE_FIELDS);

	const generator = fieldGenerator({
		libraryId,
		adType,
		showPristineErrors,
		isViewOnly,
		errors,
		languageOptions,
		ctaOptions,
		touchImageFieldArray,
		showCountError,
		isFieldArrayValid,
		onLanguageChange,
	});
	const $fields = useMemo(() => {
		return Object.keys(GOOGLE_DEMAND_GEN_IMAGE_FIELDS).map(generator);
	}, [selectedLanguage, languageOptions, ctaOptions, adType, errors]);

	return (
		<FormSection name={adType}>
			{errorDisplay}
			<div className="form-container google-demand-gen-image-asset">
				<div className="fields">
					{ $fields }
				</div>
			</div>
		</FormSection>
	);
}

GoogleDemandGenImage.displayName = "GoogleDemandGenImage";
GoogleDemandGenImage.defaultProps = {
	isViewOnly: false,
	showPristineErrors: false,
};
